import React, { useState } from 'react';
import {
  RocketLaunch as RocketIcon,
  PhoneAndroid as MobileIcon,
  Devices as CrossPlatformIcon,
  DesignServices as DesignIcon,
  BugReport as TestingIcon,
  Support as SupportIcon,
  Code as NativeIcon,
  Bolt as BoltIcon,
  ArrowRight as ArrowRightIcon,
  CheckCircle as CheckCircleIcon,
  ThumbUp as ThumbUpIcon,
  Palette as PaletteIcon,
  Terminal as TerminalIcon,
  Speed as PerformanceIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const MobAppDevelopmentPage = () => {
  const theme = useTheme();
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const services = [
    {
      icon: <DesignIcon fontSize="large" />,
      title: "Mobile App Design",
      description: "Beautiful, intuitive interfaces that enhance user engagement and reflect your brand identity.",
      image: "https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?w=500&h=300&fit=crop"
    },
    {
      icon: <CrossPlatformIcon fontSize="large" />,
      title: "Cross-Platform Development",
      description: "Single codebase solutions that work flawlessly across iOS and Android platforms.",
      image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=500&h=300&fit=crop"
    },
    {
      icon: <NativeIcon fontSize="large" />,
      title: "Native App Development",
      description: "High-performance apps built with platform-specific languages for optimal user experience.",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=500&h=300&fit=crop"
    },
    {
      icon: <TestingIcon fontSize="large" />,
      title: "App Testing & QA",
      description: "Rigorous testing protocols to ensure flawless performance across all devices.",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=500&h=300&fit=crop"
    },
    {
      icon: <SupportIcon fontSize="large" />,
      title: "App Maintenance & Support",
      description: "Ongoing updates, improvements, and technical support for your mobile application.",
      image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?w=500&h=300&fit=crop"
    }
  ];

  const benefits = [
    { icon: <BoltIcon />, title: "Expertise", description: "Skilled developers with deep understanding of mobile technologies" },
    { icon: <PaletteIcon />, title: "Custom Solutions", description: "Tailor-made apps for your specific requirements" },
    { icon: <ThumbUpIcon />, title: "User-Centric Design", description: "Intuitive interfaces that users love" },
    { icon: <TerminalIcon />, title: "Cutting-Edge Tech", description: "Latest tools and frameworks for future-proof apps" },
    { icon: <PerformanceIcon />, title: "Performance Focus", description: "Optimized for speed and smooth operation" }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        bgcolor: 'rgba(63, 81, 181, 0.9)',
        color: 'primary.contrastText',
        py: 12,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
          zIndex: 1
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Stack alignItems="center" textAlign="center" spacing={4}>
            <Chip
            onClick={()=>console.log("")}
              icon={<RocketIcon />}
              label="Crafting Your Digital Future"
              sx={{
                bgcolor: 'rgba(208, 227, 239, 0.55)',
                color: 'primary.contrastText',
                px: 2,
                py: 1,
                fontSize: '0.875rem'
              }}
            />

            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Mobile App <Box component="span" sx={{ color: '#a5d6a7' }}>Development</Box>
            </Typography>

            <Typography variant="h5" sx={{ maxWidth: 800, opacity: 0.9 }}>
              In today's interconnected world, having a powerful mobile app can be the key to success.
              We turn your ideas into innovative, feature-rich applications.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                endIcon={<ArrowRightIcon />}
                onClick={() => navigate('/get-in-touch')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Get Started
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: alpha(theme.palette.common.white, 0.3),
                  color: 'primary.contrastText',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    bgcolor: alpha(theme.palette.common.white, 0.1)
                  }
                }}

              >
                View Portfolio
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Expertise Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 3 }}>
              Elevating App Development to Craft Experiences
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              We don't just create apps; we engineer experiences. With a passionate team of professionals,
              we craft apps that not only look stunning but function seamlessly.
            </Typography>
            <List dense>
              {[
                "User-centered design approach",
                "Agile development methodology",
                "Cross-platform compatibility",
                "Rigorous quality assurance",
                "Post-launch support & maintenance"
              ].map((item, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?w=800&fit=crop"
              alt="App Development"
              sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: theme.shadows[4],
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Why Choose Us Section */}

      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Stack spacing={6} alignItems="center" textAlign="center">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 800,
              color: 'primary.main',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Why Choose ShreeshTech?
          </Typography>



          <Grid container spacing={4} justifyContent="center">
            {benefits.map((benefit, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 280, // Ensures same width
                    minHeight: 260,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 3,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: theme => theme.shadows[6],
                      bgcolor: theme => alpha(theme.palette.primary.light, 0.05),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      mb: 2,
                    }}
                  >
                    {benefit.icon}
                  </Box>

                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

        </Stack>
      </Box>


      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Stack spacing={6} alignItems="center" textAlign="center">
          <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
            Our App Development Services
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700 }}>
            Mastering app development with diverse technologies to craft customized solutions for your vision
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                <Card
                  elevation={hoveredCard === index ? 6 : 2}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    width: '100%',
                    maxWidth: 380,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={service.image}
                    alt={service.title}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                  <CardContent sx={{
                    p: { xs: 3, md: 4 },
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        mx: 'auto',
                        transition: 'all 0.3s ease',
                        ...(hoveredCard === index && {
                          transform: 'scale(1.1)',
                          bgcolor: 'secondary.main'
                        })
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        bgcolor: 'rgba(33, 150, 243, 0.9)',
        color: 'primary.contrastText',
        py: 10,
        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
      }}>
        <Container maxWidth="lg">
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
              Ready to Transform Your Ideas into Apps?
            </Typography>

            <Typography variant="h6" sx={{ maxWidth: 700, opacity: 0.9 }}>
              Your mobile app is an extension of your brand. Partner with us to turn your vision into reality.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                onClick={() => navigate('/get-in-touch')}
                color="secondary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Start Your Project
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/get-in-touch')}
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderColor: alpha(theme.palette.common.white, 0.3),
                  color: 'primary.contrastText',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    bgcolor: 'rgba(33, 150, 243, 0.9)',
                  }
                }}
              >
                Free Consultation
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default MobAppDevelopmentPage;